import { Input } from '@/components/ui/input';
import { apiClient } from '@/lib/apiClient';
import { cn } from '@/lib/utils';
import { Loader2, MapPin, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface LocationResult {
    id: number;
    name: string;
    state_name?: string;
    state_id?: number;
    type: 'city' | 'state';
    display: string;
    /** Só com searchType: slug do estado/cidade, sigla do estado e se a cidade tem coordenada (raio). */
    slug?: string | null;
    code?: string | null;
    state_code?: string | null;
    has_coordinates?: boolean;
}

interface LocationAutocompleteResponse {
    cities: LocationResult[];
    states: LocationResult[];
}

interface LocationAutocompleteProps {
    value: string;
    onChange: (value: string, location?: LocationResult) => void;
    placeholder?: string;
    className?: string;
    inputClassName?: string;
    /** Busca de resultados: ordena as cidades pelos itens ativos do módulo e usa o formato "Cidade - UF". */
    searchType?: 'embarcacoes' | 'empresas';
    autoFocus?: boolean;
}

export function LocationAutocomplete({
    value,
    onChange,
    placeholder = 'Cidade ou estado',
    className = '',
    inputClassName = '',
    searchType,
    autoFocus = false,
}: LocationAutocompleteProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [results, setResults] = useState<LocationResult[]>([]);
    const [loading, setLoading] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listboxId = useRef(`location-list-${Math.random().toString(36).substr(2, 9)}`).current;
    const debounceTimerRef = useRef<number | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const fetchLocations = useCallback(
        async (query: string) => {
            if (query.length < 2) {
                setResults([]);
                setIsOpen(false);
                return;
            }

            // Cancel previous request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            abortControllerRef.current = new AbortController();

            try {
                setLoading(true);
                const response = await apiClient.get<LocationAutocompleteResponse>('/api/locations/autocomplete', {
                    params: searchType ? { q: query, type: searchType } : { q: query },
                    signal: abortControllerRef.current.signal,
                });

                const allResults: LocationResult[] = [...response.data.states, ...response.data.cities];

                setResults(allResults);
                setIsOpen(allResults.length > 0);
            } catch (err: unknown) {
                if ((err as Error).name !== 'CanceledError') {
                    console.error('Location autocomplete error:', err);
                    setResults([]);
                }
            } finally {
                setLoading(false);
            }
        },
        [searchType],
    );

    const handleInputChange = (newValue: string) => {
        onChange(newValue);
        setSelectedIndex(-1);

        if (debounceTimerRef.current) {
            window.clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = window.setTimeout(() => {
            fetchLocations(newValue);
        }, 300);
    };

    const handleSelectLocation = (location: LocationResult) => {
        onChange(location.display, location);
        setIsOpen(false);
        setResults([]);
    };

    const handleClear = () => {
        onChange('');
        setIsOpen(false);
        setResults([]);
        inputRef.current?.focus();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen || results.length === 0) {
            if (e.key === 'Enter') {
                setIsOpen(false);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && results[selectedIndex]) {
                    handleSelectLocation(results[selectedIndex]);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                setSelectedIndex(-1);
                break;
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSelectedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            if (debounceTimerRef.current) {
                window.clearTimeout(debounceTimerRef.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    return (
        <div ref={containerRef} className={`relative ${className}`}>
            <div className="relative">
                <MapPin className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400 sm:left-4" />
                <Input
                    ref={inputRef}
                    type="text"
                    value={value}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    autoFocus={autoFocus}
                    className={cn('h-12 border-gray-300 pl-9 text-sm text-gray-700 sm:h-14 sm:pl-10 sm:text-base', inputClassName)}
                    aria-label="Buscar por cidade ou estado"
                    role="combobox"
                    aria-expanded={isOpen}
                    aria-controls={listboxId}
                    aria-autocomplete="list"
                    aria-activedescendant={selectedIndex >= 0 ? `${listboxId}-item-${selectedIndex}` : undefined}
                />
                {value && (
                    <button
                        onClick={handleClear}
                        className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label="Limpar localização"
                        type="button"
                    >
                        <X className="h-4 w-4" />
                    </button>
                )}
            </div>

            {/* Autocomplete Dropdown */}
            {isOpen && (
                <div
                    id={listboxId}
                    role="listbox"
                    className="absolute z-50 mt-2 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
                    aria-label="Resultados de localização"
                >
                    {loading && (
                        <div className="flex items-center justify-center py-8" role="status" aria-live="polite">
                            <Loader2 className="h-6 w-6 animate-spin text-blue-800" />
                            <span className="sr-only">Carregando localizações...</span>
                        </div>
                    )}

                    {!loading && results.length === 0 && value.length >= 2 && (
                        <div className="px-4 py-8 text-center text-gray-500" role="status" aria-live="polite">
                            Nenhuma localização encontrada para "{value}"
                        </div>
                    )}

                    {!loading && results.length > 0 && (
                        <div className="max-h-80 overflow-y-auto">
                            {results.map((location, index) => (
                                <button
                                    key={`${location.type}-${location.id}`}
                                    id={`${listboxId}-item-${index}`}
                                    role="option"
                                    aria-selected={selectedIndex === index}
                                    onClick={() => handleSelectLocation(location)}
                                    className={`flex w-full items-center gap-3 border-b border-gray-100 px-4 py-3 transition-colors last:border-b-0 hover:bg-blue-50 ${
                                        selectedIndex === index ? 'bg-blue-50' : ''
                                    }`}
                                    type="button"
                                >
                                    <div
                                        className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${
                                            location.type === 'state' ? 'bg-blue-100' : 'bg-green-100'
                                        }`}
                                    >
                                        <MapPin className={`h-5 w-5 ${location.type === 'state' ? 'text-blue-600' : 'text-green-600'}`} />
                                    </div>
                                    <div className="min-w-0 flex-1 text-left">
                                        <div className="flex items-center gap-2">
                                            <div className="font-medium text-gray-900">{location.display}</div>
                                            <span
                                                className={`rounded-full px-2 py-1 text-xs font-medium ${
                                                    location.type === 'state' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                                                }`}
                                            >
                                                {location.type === 'state' ? 'Estado' : 'Cidade'}
                                            </span>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
